import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { InputWithLabel } from "../../components/micro/input";
import {
  createQAFAQSetting,
  createTopicFAQSetting,
  getFAQList,
} from "../../redux/masterSetting/slice";

const AddEditFAQ = ({
  onClose,
  isType,
  modalType,
  selectedFAQId,
  faqListData,
  searchValue,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const selectedTopic = Array.isArray(faqListData?.data)
    ? faqListData.data.find((faq) => faq.id === selectedFAQId)
    : null;

  const selectedQuestion =
    selectedFAQId?.questionId && selectedFAQId?.topicId
      ? faqListData?.data
          .find((faq) => faq.id === selectedFAQId.topicId)
          ?.questions.find((q) => q.id === selectedFAQId.questionId)
      : null;

  const validationSchema = Yup.object().shape(
    modalType === "topic"
      ? {
          topicName: Yup.string()
            .trim()
            .min(3, "Topic name must be at least 3 characters")
            .required("Please enter topic name"),
        }
      : {
          question: Yup.string()
            .trim()
            .min(3, "Question must be at least 3 characters")
            .required("Please enter question"),
          answer: Yup.string()
            .trim()
            .min(3, "Answer must be at least 3 characters")
            .required("Please enter answer"),
        }
  );

  const handleSubmit = async (values) => {
    setIsLoading(true);

    const topicId = selectedFAQId?.topicId || selectedTopic?.id;

    const action =
      modalType === "topic"
        ? createTopicFAQSetting({
            id: selectedFAQId,
            title: values?.topicName,
          })
        : createQAFAQSetting({
            id: selectedFAQId?.questionId,
            topic_id: topicId,
            question: values?.question,
            answer: values?.answer,
          });

    try {
      const res = await dispatch(action);
      if (
        res.type === "createTopicFAQSetting/fulfilled" ||
        res.type === "createQAFAQSetting/fulfilled"
      ) {
        dispatch(getFAQList({ search: searchValue }));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
      onClose();
    }
  };

  return (
    <>
      <div className="model" onClick={onClose}>
        <div
          className="bg-white items-center justify-center drop-shadow-lg rounded-lg w-full max-w-3xl py-8 px-6 mx-auto relative"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {modalType === "topic" ? (
            <h4 className="font-Montserrat text-xl font-normal">
              {isType === "edit" ? "Rename Topic" : "Add Topic"}
            </h4>
          ) : (
            <h4 className="font-Montserrat text-xl font-normal">
              {isType === "edit" ? "Edit Q&A" : "Add New Q&A"}
            </h4>
          )}

          <Formik
            enableReinitialize={true}
            initialValues={
              modalType === "topic"
                ? { topicName: selectedTopic ? selectedTopic.topic_title : "" }
                : {
                    question: selectedQuestion ? selectedQuestion.question : "",
                    answer: selectedQuestion?.answer?.[0]?.answer || "",
                  }
            }
            validationSchema={validationSchema}
            onSubmit={(values) => {
              setFormValues(values);
              setShowConfirmationModal(true);
            }}
          >
            {({ values, errors, touched, handleBlur, handleChange }) => (
              <Form>
                <div className="mt-4">
                  {modalType === "topic" && (
                    <div>
                      <InputWithLabel
                        className={`${
                          errors.topicName && touched.topicName
                            ? `border border-red`
                            : `border border-borderTheme/80 focus-within:border-themeBtn `
                        }  transition duration-300 ease-in-out w-full text-black border hover:border-themeBtn p-2`}
                        idFromName="topicName"
                        label="Topic Name*"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.topicName}
                        errors={errors.topicName && touched.topicName}
                        errorsText={errors.topicName}
                      />
                    </div>
                  )}

                  {modalType !== "topic" && (
                    <div>
                      <div className="mb-4">
                        <InputWithLabel
                          className={`${
                            errors.question && touched.question
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out w-full text-black border hover:border-themeBtn p-2`}
                          idFromName="question"
                          label="Question*"
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.question}
                          errors={errors.question && touched.question}
                          errorsText={errors.question}
                        />
                      </div>
                      <InputWithLabel
                        className={`${
                          errors.answer && touched.answer
                            ? `border border-red`
                            : `border border-borderTheme/80 focus-within:border-themeBtn `
                        }  transition duration-300 ease-in-out w-full text-black border hover:border-themeBtn p-2`}
                        idFromName="answer"
                        label="Answer*"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.answer}
                        errors={errors.answer && touched.answer}
                        errorsText={errors.answer}
                      />
                    </div>
                  )}
                </div>

                {/* Submit button */}
                <div className="flex justify-end gap-3 mt-6 w-full">
                  <button
                    onClick={onClose}
                    type="button"
                    className=" text-themeBtn w-full flex items-center uppercase font-Montserrat justify-center px-6 py-1.5 rounded border border-themeBtn"
                  >
                    <span>Cancel</span>
                  </button>
                  <button
                    type="submit"
                    className="flex items-center w-full justify-center px-6 py-1.5 rounded font-Montserrat uppercase border-2 border-themeBtn  text-white bg-themeBtn"
                  >
                    <span>Confirm</span>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        {showConfirmationModal && (
          <div>
            <ConfirmationModel
              confirmText={
                modalType === "topic"
                  ? isType === "edit"
                    ? "Rename Topic"
                    : "Add Topic"
                  : isType === "edit"
                  ? "Edit"
                  : "Add Q&A"
              }
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={() => handleSubmit(formValues)}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AddEditFAQ;
